import React, { useEffect, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { BaseLayoutContext, BaseLayoutSection } from '../../types/base-layout'
import { PrayerRequest } from '../../types/base-content'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PrayerRequestsSectionProps {
  section: BaseLayoutSection
  context: BaseLayoutContext
}

// Get a random set of 3 prayer requests
function getRandomPrayerRequests(requests: PrayerRequest[], count: number = 3): PrayerRequest[] {
  if (requests.length === 0) return []
  if (requests.length <= count) return [...requests]
  
  // Shuffle the array and take the first 'count' items
  const shuffled = [...requests]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  
  return shuffled.slice(0, count)
}

export function PrayerRequestsSection({ section, context }: PrayerRequestsSectionProps) {
  const prayerRequests = useMemo(() => context.prayerRequests ?? [], [context.prayerRequests])
  const [displayRequests, setDisplayRequests] = useState<PrayerRequest[]>([])

  // Set initial random set when prayer requests change
  useEffect(() => {
    if (prayerRequests.length > 0) {
      setDisplayRequests(getRandomPrayerRequests(prayerRequests))
    } else {
      setDisplayRequests([])
    }
  }, [prayerRequests])

  const goToIndex = (direction: number) => {
    if (prayerRequests.length === 0) return
    // Get a new random set of 3 requests each time
    setDisplayRequests(getRandomPrayerRequests(prayerRequests))
  }

  return (
    <Card key={section.id}>
      <CardHeader>
        <CardTitle>{section.title ?? 'Prayer Requests'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {displayRequests.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No prayer requests available at this time.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => goToIndex(-1)}
                disabled={prayerRequests.length <= 3}
                showLoader={false}
                className="h-8 w-8 flex-shrink-0"
                aria-label="Previous prayer requests"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex-1 space-y-3">
                {displayRequests.map((request, idx) => (
                  <div
                    key={`${request.id}-${request.type}-${idx}`}
                    className="rounded-md border border-border bg-muted/40 p-4"
                  >
                    <p className="text-xs font-medium text-foreground uppercase tracking-wide mb-2">
                      {request.type === 'homeowner' ? 'Homeowner Prayer Request' : 'Volunteer Prayer Request'}
                    </p>
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {request.prayerRequest}
                    </p>
                  </div>
                ))}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => goToIndex(1)}
                disabled={prayerRequests.length <= 3}
                showLoader={false}
                className="h-8 w-8 flex-shrink-0"
                aria-label="Next prayer requests"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

